import React, { useState } from 'react';
import type { ContractedItem } from '@/hooks/useContractedItems';

interface ContractedItemDetailsProps {
  item: ContractedItem;
  optionalNames?: Record<string, string>;
  /** Não repete a lista de escolhas do menu por seleção (quando o chamador já a exibe). */
  skipChoices?: boolean;
  className?: string;
  /** Mostra só um resumo (contagens por categoria) e abre a lista completa ao toque. */
  collapsible?: boolean;
  /** Cor do fornecedor (botão) para os selos e o link do resumo. */
  accent?: string;
}

/** Descrição completa do que o cliente contratou: categorias, escolhas, trocas, itens incluídos e adicionais. */
export const ContractedItemDetails = ({ item, optionalNames = {}, skipChoices = false, className = '', collapsible = false, accent }: ContractedItemDetailsProps) => {
  const [expanded, setExpanded] = useState(false);
  const optionals = item.optionalIds.map((id) => optionalNames[id]).filter(Boolean);
  const hasAny =
    item.groups.length > 0 ||
    (!skipChoices && item.choices.length > 0) ||
    item.substitutions.length > 0 ||
    item.additions.length > 0 ||
    item.optionalIds.length > 0;
  if (!hasAny) return null;

  const showFull = !collapsible || expanded;
  const pills: string[] = [
    ...item.groups.map((g) => `${g.choices.reduce((sum, c) => sum + c.count, 0)} ${g.label.toLowerCase()}`),
    ...(item.substitutions.length > 0 ? [`${item.substitutions.length} ${item.substitutions.length === 1 ? 'troca' : 'trocas'}`] : []),
    ...(item.additions.length > 0 ? [`${item.additions.length} ${item.additions.length === 1 ? 'item incluído' : 'itens incluídos'}`] : []),
    ...(item.optionalIds.length > 0 ? [`${item.optionalIds.length} ${item.optionalIds.length === 1 ? 'adicional' : 'adicionais'}`] : []),
  ];

  return (
    <div className={`space-y-2 text-xs text-muted-foreground ${className}`}>
      {collapsible && (
        <div>
          <div className="flex flex-wrap gap-1">
            {pills.map((label, i) => (
              <span
                key={i}
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${accent ? '' : 'bg-primary/10 text-primary'}`}
                style={accent ? { backgroundColor: `${accent}1A`, color: accent } : undefined}
              >
                {label}
              </span>
            ))}
          </div>
          <button
            type="button"
            className="mt-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          >
            {expanded ? 'Ocultar itens do cardápio ▴' : 'Ver itens do cardápio ▾'}
          </button>
        </div>
      )}
      {showFull && item.groups.map((g) => (
        <div key={g.id}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/80">
            {g.label} <span className="font-normal text-muted-foreground">({g.choices.reduce((s, c) => s + c.count, 0)})</span>
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {g.choices.map((c, i) => (
              <li key={i} className="leading-snug break-words">
                <span className="font-medium text-foreground">{c.count > 1 ? `${c.count}x ` : ''}{c.name}</span>
                {c.detail && <span> · {c.detail}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {showFull && !skipChoices && item.choices.length > 0 && (
        <ul className="space-y-0.5">
          {item.choices.map((c, i) => (
            <li key={i} className="leading-snug break-words">
              <span className="font-medium text-foreground">{c.count > 1 ? `${c.count}x ` : ''}{c.name}</span>
              {c.detail && <span> · {c.detail}</span>}
            </li>
          ))}
        </ul>
      )}

      {showFull && item.substitutions.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/80">Trocas</p>
          <ul className="mt-0.5 space-y-0.5">
            {item.substitutions.map((s, i) => <li key={i} className="leading-snug break-words">{s}</li>)}
          </ul>
        </div>
      )}

      {showFull && item.additions.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/80">Itens incluídos</p>
          <ul className="mt-0.5 space-y-0.5">
            {item.additions.map((a, i) => <li key={i} className="leading-snug break-words">{a}</li>)}
          </ul>
        </div>
      )}

      {showFull && item.optionalIds.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground/80">
            Adicionais contratados ({item.optionalIds.length})
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {(optionals.length > 0 ? optionals : item.optionalIds.map(() => 'Adicional')).map((n, i) => (
              <li key={i} className="leading-snug break-words font-medium text-foreground">{n}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ContractedItemDetails;
